import React from 'react';
import { useSelector } from 'react-redux';
import {
  InputActionMeta,
  MultiValue,
  MultiValueProps,
  Options,
  OptionsOrGroups,
} from 'react-select';
import { ElementOf } from 'ts-essentials';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { useLazyQuery } from '@apollo/client';
import { GET_FIELD_VALUES } from '../../../../../../pages/Formulas/apollo';
import { activeSchemaIdSelector } from '../../../../../../redux/selectors/builder';
import {
  IEnumBucketFieldsFragment,
  IGetFieldValuesQuery,
  IGetFieldValuesQueryVariables,
  PermissionKey,
} from '../../../../../../utils/apollo/graphqlSchema';
import { defaultEventInput } from '../../../../../../utils/builder';
import { CreatableReactSelectMenu } from '../../../../../Dropdown/StyledReactSelect';
import { BasicEntity } from '../../../../../EntityLabels';
import { MAX_FIELD_VALUE_LENGTH } from '../../../../../../config/constants';
import usePermission from '../../../../../../utils/hooks/permissionHooks/usePermission';
import { byTypeName } from '../../../../../../utils/types';
type IEnumBucketFieldValues = IEnumBucketFieldsFragment['values'];

interface IProps {
  selectedFieldValues: IEnumBucketFieldValues;
  setSelectedFieldValues: (fieldValues: IEnumBucketFieldValues) => void;
  /**
   * used to fetch field values if it's breakdown
   * if it's histogram, field key is empty string
   */
  fieldKey?: string;
}

type IMenuOption = ElementOf<IEnumBucketFieldValues>;

// TODO: This component is almost identical to CreatableOperandMenu
// (and to some extent to SignificanceFieldValueMenu). Its migration is not
// straightforward because it may be used with subject key in fieldKey property
// while CreatableOperandMenu needs IFieldFieldsFragment
// (+ Filter Widget context)
export default ({
  selectedFieldValues,
  setSelectedFieldValues,
  fieldKey,
}: IProps) => {
  const activeSchemaId = useSelector(activeSchemaIdSelector);

  const { checkIsPermitted } = usePermission();
  const isPermittedToReadFields = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FieldRead],
    schemaId: activeSchemaId,
  });

  const [loadFieldValues, { data, called, loading, refetch }] = useLazyQuery<
    IGetFieldValuesQuery,
    IGetFieldValuesQueryVariables
  >(GET_FIELD_VALUES, {
    variables: {
      term: '',
      fieldKey: fieldKey ?? '',
      events: [defaultEventInput],
      parents: [],
      schemaId: activeSchemaId,
      includeValues: [],
    },
  });

  const handleSelectOptions = (newOptions: MultiValue<IMenuOption>) => {
    if (!newOptions?.length) {
      setSelectedFieldValues([]);

      return;
    }

    const values = [...newOptions];

    setSelectedFieldValues(values);
  };

  const handleValidateNewOption = (
    inputValue: string,
    value: Options<IMenuOption>,
    options: OptionsOrGroups<IMenuOption, never>,
  ) => {
    if (!inputValue?.length) {
      return false;
    }

    // Check length
    if (inputValue.length > MAX_FIELD_VALUE_LENGTH) {
      return false;
    }

    // Check menu value
    if (
      value.some(
        (option) => option.name.toLowerCase() === inputValue.toLowerCase(),
      )
    ) {
      return false;
    }

    // Check options
    if (
      options?.length &&
      options.some(
        (option) => option.name.toLowerCase() === inputValue.toLowerCase(),
      )
    ) {
      return false;
    }

    return true;
  };

  const handleCreateNewOption = (inputValue: string) => {
    setSelectedFieldValues([
      ...selectedFieldValues,
      {
        __typename: 'BasicFieldValue',
        key: inputValue,
        name: inputValue,
      },
    ]);
  };

  const renderMultiValue = (
    props: MultiValueProps<IMenuOption, true, never>,
  ) => {
    const { children, removeProps } = props;

    return (
      <BasicEntity onClear={removeProps.onClick ?? noop}>
        {children}
      </BasicEntity>
    );
  };

  const handleSearch = debounce((term: string, actionMeta: InputActionMeta) => {
    // Using actionMeta to detect cases to avoid refetch when:
    // 1. Input hasn't been changed and user opens-closes menu;
    // 2. Input hasn't been changed and user selects an option;
    if (actionMeta.action === 'menu-close' && !actionMeta.prevInputValue) {
      return;
    }

    refetch({ term });
  }, 200);

  const handleLoadOptions = () => {
    if (!called && isPermittedToReadFields) {
      loadFieldValues();
    }
  };

  const options =
    data?.fieldValues?.filter(byTypeName('BasicFieldValue')) ?? [];

  return (
    <CreatableReactSelectMenu
      // selecting next menu by tab
      tabIndex={1}
      openMenuOnFocus
      isMulti={true}
      isLoading={loading}
      menuPosition="fixed"
      menuPortalTarget={document.body}
      closeMenuOnSelect
      hideSelectedOptions
      onChange={handleSelectOptions}
      onInputChange={handleSearch}
      onMenuOpen={handleLoadOptions}
      options={options}
      value={selectedFieldValues}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.key}
      // Create new option props:
      isValidNewOption={handleValidateNewOption}
      onCreateOption={handleCreateNewOption}
      // option may be creatable - that's why we need 'label' in option check
      formatOptionLabel={(option) =>
        option?.name || ('label' in option ? option.label : null)
      }
      components={{ MultiValue: renderMultiValue }}
      styles={{
        control: (base) => ({
          ...base,
          minWidth: '100%',
        }),
      }}
    />
  );
};
